// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {useCursorAtEnd} from '@app/features/app/hooks/useCursorAtEnd';
import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/Input';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import styles from '@app/features/user/components/modals/BaseChangeNicknameModal.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {XIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';
import {useForm} from 'react-hook-form';

interface FormInputs {
	nick: string;
}
interface BaseChangeNicknameModalProps {
	currentNick: string;
	displayName: string;
	onSave: (nick: string | null) => Promise<void>;
}
export const BaseChangeNicknameModal: React.FC<BaseChangeNicknameModalProps> = observer(
	({currentNick, displayName, onSave}) => {
		const {t} = useLingui();
		const form = useForm<FormInputs>({
			defaultValues: {
				nick: currentNick,
			},
		});
		const nickRef = useCursorAtEnd<HTMLInputElement>();
		const onSubmit = useCallback(
			async (data: FormInputs) => {
				const nick = data.nick.trim() || null;
				await onSave(nick);
				ToastCommands.createToast({
					type: 'success',
					children: <Trans>Nickname updated</Trans>,
				});
				ModalCommands.pop();
			},
			[onSave],
		);
		const {handleSubmit, isSubmitting} = useFormSubmit({
			form,
			onSubmit,
			defaultErrorField: 'nick',
		});
		const nickValue = form.watch('nick');
		return (
			<Modal.Root size="small" centered>
				<Form form={form} onSubmit={handleSubmit} aria-label={t`Change nickname form`}>
					<Modal.Header title={t`Change Nickname`} />
					<Modal.Content>
						<Modal.ContentLayout>
							<Input
								{...form.register('nick', {
									maxLength: {
										value: 32,
										message: t`Nickname must not exceed 32 characters`,
									},
								})}
								ref={(el) => {
									nickRef(el);
									form.register('nick').ref(el);
								}}
								autoFocus={true}
								type="text"
								label={t`Nickname`}
								placeholder={displayName}
								maxLength={32}
								error={form.formState.errors.nick?.message}
								rightElement={
									nickValue ? (
										<FocusRing offset={-2}>
											<button
												type="button"
												className={styles.clearButton}
												onClick={() => form.setValue('nick', '')}
												aria-label={t`Clear nickname`}
											>
												<XIcon size={16} weight="bold" />
											</button>
										</FocusRing>
									) : undefined
								}
							/>
						</Modal.ContentLayout>
					</Modal.Content>
					<Modal.Footer>
						<Button type="submit" submitting={isSubmitting}>
							<Trans>Save</Trans>
						</Button>
					</Modal.Footer>
				</Form>
			</Modal.Root>
		);
	},
);
